import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  Space,
  message,
  Card,
  Row,
  Col,
  InputNumber,
  Table,
  Popconfirm,
  Typography,
  Divider,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { InfiniteSelect } from '../../Component/InfiniteSelect';
import Api from '../../Network';
import dayjs from 'dayjs';
import CustomerClassificationModal from '../Customers/CustomerClassificationModal';

const { TextArea } = Input;
const { Text } = Typography;

interface CreateOrderModalProps {
  visible: boolean;
  onClose: () => void;
  onOrderCreated: () => void;
  onSwitchToNewOrders?: () => void;
}

interface LineItem {
  key: string;
  product_id: number | null;
  product_name?: string;
  product?: any;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_value: number;
  total: number;
  total_after_tax: number;
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  visible,
  onClose,
  onOrderCreated,
  onSwitchToNewOrders,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [classificationModalVisible, setClassificationModalVisible] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState<any>(null);
  const [customerNotClassified, setCustomerNotClassified] = useState(false);
  const [checkingClassification, setCheckingClassification] = useState(false);

  // Add new line item
  const addLineItem = () => {
    const newItem: LineItem = {
      key: Date.now().toString(),
      product_id: null,
      quantity: 1,
      unit_price: 0,
      tax_rate: 15,
      tax_value: 0,
      total: 0,
      total_after_tax: 0,
    };
    setLineItems([...lineItems, newItem]);
  };

  // Remove line item
  const removeLineItem = (key: string) => {
    setLineItems(lineItems.filter((item) => item.key !== key));
  };

  // Update line item
  const updateLineItem = (key: string, field: keyof LineItem, value: any) => {
    setLineItems((prevLineItems) => {
      const newLineItems = prevLineItems.map((item) => {
        if (item.key === key) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unit_price') {
            updatedItem.total = (updatedItem.quantity || 0) * (updatedItem.unit_price || 0);
          }
          // Calculate tax
          if (field === 'tax_rate' || field === 'quantity' || field === 'unit_price') {
            updatedItem.tax_value = updatedItem.total * ((updatedItem.tax_rate || 0) / 100);
            updatedItem.total_after_tax = updatedItem.total + updatedItem.tax_value;
          }
          return updatedItem;
        }
        return item;
      });
      return newLineItems;
    });
  };

  // Handle product selection
  const handleProductSelect = (key: string, productId: number, product: any) => {
    updateLineItem(key, 'product_id', productId);
    updateLineItem(key, 'product_name', product.name_ar || product.name_en);
    updateLineItem(key, 'product', product);

    if (product.selling_price) {
      updateLineItem(key, 'unit_price', parseFloat(product.selling_price));
    }
  };

  // Calculate order totals
  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_value || 0), 0);
    const grandTotal = lineItems.reduce((sum, item) => sum + (item.total_after_tax || 0), 0);
    return { subtotal, totalTax, grandTotal };
  };

  // Check customer classification
  const checkCustomerClassification = async (customerId: number) => {
    try {
      setCheckingClassification(true);
      const response: any = await Api.get(`/api/admin/customers/${customerId}`);
      const customer = response?.data?.customer || response?.data?.data || response?.data;
      const hasClassification =
        customer?.customer_classification_id !== null &&
        customer?.customer_classification_id !== undefined;
      setCustomerNotClassified(!hasClassification);
      if (hasClassification) {
        setSelectedCustomer(customer);
      } else {
        setSelectedCustomer(customer);
      }
      return hasClassification;
    } catch (error) {
      setCustomerNotClassified(false);
      return false;
    } finally {
      setCheckingClassification(false);
    }
  };

  // Handle customer selection change
  const handleCustomerChange = async (customerId: number, option: any) => {
    const customer = option?.item || option;
    setSelectedCustomer(customer);
    setCustomerNotClassified(false);

    // Update form field value
    form.setFieldsValue({
      customer_id: customerId,
    });

    if (customerId) {
      // Check if customer has classification
      await checkCustomerClassification(customerId);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (lineItems.length === 0) {
      message.error(t('orders.validation.addAtLeastOneItem', 'Please add at least one product'));
      return;
    }

    const invalidItems = lineItems.filter(
      (item) => !item.product_id || item.quantity <= 0 || item.unit_price <= 0
    );

    if (invalidItems.length > 0) {
      message.error(t('orders.validation.completeAllItems', 'Please complete all line items'));
      return;
    }

    const customerId = selectedCustomer?.id || values.customer_id;
    if (!customerId) {
      message.error(t('orders.validation.selectCustomer', 'Please select a customer'));
      return;
    }

    // Check if customer has classification
    if (customerNotClassified || !selectedCustomer?.customer_classification_id) {
      // Re-check classification
      const hasClassification = await checkCustomerClassification(customerId);
      if (!hasClassification) {
        setPendingOrderData({
          date: values.date.format('DD-MM-YYYY'),
          driver_id: values.driver_id?.toString(),
          location: values.location,
          branch_id: customerId?.toString(),
          notes: values.notes || '',
          customer_id: customerId,
          line_items: lineItems.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price.toString(),
          })),
        });
        setClassificationModalVisible(true);
        message.warning(
          t(
            'orders.validation.classifyCustomerFirst',
            'Please classify the customer before creating the order'
          )
        );
        return;
      }
    }

    // Proceed with order creation
    await createOrder(values);
  };

  // Create order after classification is confirmed
  const createOrder = async (values: any) => {
    setLoading(true);
    try {
      const orderData = {
        order: {
          date: values.date.format('DD-MM-YYYY'),
          driver_id: values.driver_id?.toString(),
          location: values.location,
          branch_id: selectedCustomer?.id?.toString() || values.customer_id?.toString(),
          notes: values.notes || '',
          customer_id: values.customer_id,
          line_items: lineItems.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price.toString(),
          })),
        },
      };

      const response: any = await Api.post('/api/admin/orders', orderData);

      if (response && response.message && response.message.includes('successfully')) {
        message.success(t('orders.messages.orderCreated', 'Order created successfully'));
        handleClose();
        onOrderCreated();
        onSwitchToNewOrders?.();
      } else {
        message.error(
          t('orders.messages.orderCreateFailed', 'Failed to create order. Please try again.')
        );
      }
    } catch (error) {
      // Error creating order
    } finally {
      setLoading(false);
    }
  };

  // Handle classification modal success
  const handleClassificationSuccess = async () => {
    setClassificationModalVisible(false);

    // Refresh customer data to get updated classification
    if (selectedCustomer?.id) {
      try {
        const response: any = await Api.get(`/api/admin/customers/${selectedCustomer.id}`);
        const customer = response?.data?.customer || response?.data?.data || response?.data;
        setSelectedCustomer(customer);

        // Update form field with customer ID so the select shows the name
        form.setFieldsValue({
          customer_id: customer.id,
        });

        const hasClassification =
          customer?.customer_classification_id !== null &&
          customer?.customer_classification_id !== undefined;
        setCustomerNotClassified(!hasClassification);
      } catch (error) {
        // Error fetching customer
      }
    }

    if (pendingOrderData) {
      // Refresh customer data and create order
      try {
        const response: any = await Api.get(`/api/admin/customers/${pendingOrderData.customer_id}`);
        const customer = response?.data?.customer || response?.data?.data || response?.data;
        setSelectedCustomer(customer);

        // Update form field with customer ID
        form.setFieldsValue({
          customer_id: customer.id,
        });

        setCustomerNotClassified(false);

        const orderData = {
          order: pendingOrderData,
        };
        const createResponse: any = await Api.post('/api/admin/orders', orderData);
        if (
          createResponse &&
          createResponse.message &&
          createResponse.message.includes('successfully')
        ) {
          message.success(t('orders.messages.orderCreated', 'Order created successfully'));
          handleClose();
          onOrderCreated();
          onSwitchToNewOrders?.();
        }
      } catch (error) {
        message.error(
          t('orders.messages.orderCreateFailed', 'Failed to create order. Please try again.')
        );
      }
      setPendingOrderData(null);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setLineItems([]);
    setSelectedCustomer(null);
    setCustomerNotClassified(false);
    setPendingOrderData(null);
    onClose();
  };

  // Reset classification check when modal closes
  useEffect(() => {
    if (!visible) {
      setCustomerNotClassified(false);
      setSelectedCustomer(null);
    }
  }, [visible]);

  // Line items table columns
  const lineItemColumns = [
    {
      title: t('orders.createOrder.product', 'Product'),
      dataIndex: 'product_id',
      className: 'col-product',
      width: 300,
      'data-label': t('orders.createOrder.product', 'Product'),
      render: (productId: number | null, record: LineItem) => {
        const currentItem = lineItems.find((item) => item.key === record.key);
        if (!currentItem) return null;

        return (
          <div data-label={t('orders.createOrder.product', 'Product')}>
            <InfiniteSelect
              queryKey="api/admin/products"
              placeholder={t('orders.placeholders.selectProduct', 'Select product...')}
              showSearch
              singleMode={true}
              valueField="id"
              searchField="name_ar,name_en"
              style={{ width: '100%' }}
              initialOptions={currentItem.product ? [currentItem.product] : []}
              onChange={(productId: number, option: any) => {
                const product = option?.item || option;
                if (product && productId) {
                  handleProductSelect(currentItem.key, productId, product);
                }
              }}
              labelRender={(option: any) => {
                const product = option.item || option;
                return `${product.name_ar || product.name_en} - ${product.selling_price || 0} ${t('common.SAR')}`;
              }}
              key={`product-select-${currentItem.key}-${currentItem.product_id || 'no-product'}-${currentItem.product?.name_ar || currentItem.product?.name_en || 'no-name'}`} // Force re-render when product changes
            />
            {/* Show selected product info */}
            {currentItem.product && (
              <div className="selected-product-hint">
                <Text type="secondary">
                  {currentItem.product.name_ar || currentItem.product.name_en}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders.createOrder.quantity', 'Quantity'),
      dataIndex: 'quantity',
      width: 100,
      className: 'col-qty',
      'data-label': t('orders.createOrder.quantity', 'Quantity'),
      render: (value: number, record: LineItem) => (
        <div data-label={t('orders.createOrder.quantity', 'Quantity')}>
          <InputNumber
            min={1}
            value={value}
            onChange={(val) => updateLineItem(record.key, 'quantity', val || 1)}
            style={{ width: '100%' }}
          />
        </div>
      ),
    },
    {
      title: t('orders.createOrder.unitPrice', 'Unit Price'),
      dataIndex: 'unit_price',
      width: 130,
      className: 'col-price',
      'data-label': t('orders.createOrder.unitPrice', 'Unit Price'),
      render: (value: number, record: LineItem) => (
        <div data-label={t('orders.createOrder.unitPrice', 'Unit Price')}>
          <InputNumber
            min={0}
            step={0.01}
            value={value}
            onChange={(val) => updateLineItem(record.key, 'unit_price', val || 0)}
            style={{ width: '100%' }}
            formatter={(value) => `${value} ${t('common.SAR')}`}
            parser={(value) => parseFloat(value!.replace(t('common.SAR'), ''))}
          />
        </div>
      ),
    },
    {
      title: t('orders.createOrder.total', 'Total'),
      dataIndex: 'total',
      className: 'col-total',
      width: 120,
      'data-label': t('orders.createOrder.total', 'Total'),
      render: (value: number) => (
        <div data-label={t('orders.createOrder.total', 'Total')}>
          <Text strong>
            {value.toFixed(2)} {t('common.SAR')}
          </Text>
        </div>
      ),
    },
    {
      title: t('orders.createOrder.taxRate', 'Tax Rate (%)'),
      dataIndex: 'tax_rate',
      className: 'col-tax-rate',
      width: 120,
      'data-label': t('orders.createOrder.taxRate', 'Tax Rate (%)'),
      render: (value: number, record: LineItem) => (
        <div data-label={t('orders.createOrder.taxRate', 'Tax Rate (%)')}>
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            value={15}
            onChange={(val = 15) => updateLineItem(record.key, 'tax_rate', val || 0)}
            style={{ width: '100%' }}
            disabled
            suffix="%"
          />
        </div>
      ),
    },
    {
      title: t('orders.createOrder.taxValue', 'قيمة الضريبة'),
      dataIndex: 'tax_value',
      className: 'col-tax',
      width: 140,
      'data-label': t('orders.createOrder.taxValue', 'قيمة الضريبة'),
      render: (value: number) => (
        <div data-label={t('orders.createOrder.taxValue', 'قيمة الضريبة')}>
          <Text strong>
            {(value || 0).toFixed(2)} {t('common.SAR')}
          </Text>
        </div>
      ),
    },
    {
      title: t('orders.createOrder.totalAfterTax', 'السعر ما بعد الضريبة'),
      dataIndex: 'total_after_tax',
      className: 'col-grand',
      width: 160,
      'data-label': t('orders.createOrder.totalAfterTax', 'السعر ما بعد الضريبة'),
      render: (value: number) => (
        <div data-label={t('orders.createOrder.totalAfterTax', 'السعر ما بعد الضريبة')}>
          <Text strong style={{ color: '#1890ff' }}>
            {(value || 0).toFixed(2)} {t('common.SAR')}
          </Text>
        </div>
      ),
    },
    {
      title: t('common.actions', 'Actions'),
      width: 80,
      className: 'col-actions',
      'data-label': t('common.actions', 'Actions'),
      render: (_: any, record: LineItem) => (
        <div data-label={t('common.actions', 'Actions')}>
          <Popconfirm
            title={t('orders.confirmDeleteItem', 'Are you sure you want to remove this item?')}
            onConfirm={() => removeLineItem(record.key)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  //   const { subtotal, totalTax, grandTotal } = calculateTotals();

  return (
    <Modal
      title={
        <div className="create-order-modal-title">
          <ShoppingCartOutlined />
          <span className="title-text">{t('orders.createOrder.title', 'Create New Order')}</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="90vw"
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      className="create-order-modal order-modal-enhanced"
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          date: dayjs(),
          quantity: 1,
        }}
        className="responsive-form"
      >
        <Row gutter={[16, 16]}>
          {/* Customer Selection */}
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('orders.createOrder.customer', 'Customer')}
              name="customer_id"
              rules={[
                {
                  required: true,
                  message: t('orders.validation.selectCustomer', 'Please select a customer'),
                },
              ]}
            >
              <InfiniteSelect
                queryKey="api/admin/customers"
                placeholder={t('orders.placeholders.selectCustomer', 'Select customer...')}
                showSearch
                singleMode={true}
                valueField="id"
                searchField="name"
                onChange={handleCustomerChange}
                initialOptions={selectedCustomer ? [selectedCustomer] : []}
                labelRender={(option: any) => {
                  const customer = option.item || option;
                  return `${customer.name} - ${customer.phone_number || ''}`;
                }}
              />
              {checkingClassification && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {t('orders.checkingClassification', 'Checking customer classification...')}
                </div>
              )}
              {customerNotClassified && selectedCustomer && (
                <Alert
                  message={t('orders.customerNotClassified', 'Customer Not Classified')}
                  description={t(
                    'orders.customerNotClassifiedDescription',
                    'This customer must be classified before creating an order. Please classify the customer to continue.'
                  )}
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                  action={
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        setClassificationModalVisible(true);
                      }}
                    >
                      {t('orders.classifyCustomer', 'Classify Customer')}
                    </Button>
                  }
                />
              )}
            </Form.Item>
          </Col>

          {/* Driver Selection */}
          <Col xs={24} sm={12}>
            <Form.Item
              label={t('orders.createOrder.driver', 'Driver')}
              name="driver_id"
              rules={[
                {
                  required: true,
                  message: t('orders.validation.selectDriver', 'Please select a driver'),
                },
              ]}
            >
              <InfiniteSelect
                queryKey="api/admin/drivers"
                placeholder={t('orders.placeholders.selectDriver', 'Select driver...')}
                showSearch
                singleMode={true}
                valueField="id"
                searchField="name"
                outerFilters={{
                  show_all: 'false',
                }}
                labelRender={(option: any) => {
                  const driver = option.item || option;
                  return `${driver.name || driver.title || 'Unknown'}${driver.phone ? ` (${driver.phone})` : driver.phone_number ? ` (${driver.phone_number})` : ''}`;
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {/* Date */}
          <Col xs={24} sm={8}>
            <Form.Item
              label={t('orders.createOrder.date', 'Date')}
              name="date"
              rules={[
                {
                  required: true,
                  message: t('orders.validation.selectDate', 'Please select a date'),
                },
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>

          {/* Location */}
          <Col xs={24} sm={16}>
            <Form.Item
              label={t('orders.createOrder.location', 'Location')}
              name="location"
              rules={[
                {
                  required: true,
                  message: t('orders.validation.enterLocation', 'Please enter location'),
                },
              ]}
            >
              <Input
                placeholder={t('orders.placeholders.location', 'Enter delivery location...')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Notes */}
        <Form.Item label={t('orders.createOrder.notes', 'Notes')} name="notes">
          <TextArea
            rows={3}
            placeholder={t('orders.placeholders.notes', 'Enter any additional notes...')}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Divider />

        {/* Line Items Section */}
        <Card
          title={t('orders.createOrder.orderItems', 'Order Items')}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addLineItem}
              className="add-item-button"
            >
              {t('orders.createOrder.addItem', 'Add Item')}
            </Button>
          }
          className="order-card-enhanced add-items-section-enhanced"
        >
          {lineItems.length > 0 ? (
            <>
              <Table
                columns={lineItemColumns}
                dataSource={lineItems}
                pagination={false}
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 1150 }}
                className="line-items-table order-table-enhanced order-items-table-enhanced"
              />

              {/* Order Summary */}
              <div className="order-summary-enhanced">
                <div className="summary-row">
                  <span className="summary-label">
                    {t('orders.createOrder.itemsCount', 'Items Count')}:
                  </span>
                  <span className="summary-value">
                    {lineItems.length} {t('orders.createOrder.items', 'items')}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">
                    {t('orders.createOrder.subtotal', 'Subtotal')}:
                  </span>
                  <span className="summary-value">
                    {calculateTotals().subtotal.toFixed(2)} {t('common.SAR')}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">
                    {t('orders.createOrder.totalTax', 'Total Tax')}:
                  </span>
                  <span className="summary-value">
                    {calculateTotals().totalTax.toFixed(2)} {t('common.SAR')}
                  </span>
                </div>
                <div className="summary-row total-row">
                  <span className="summary-label">
                    {t('orders.createOrder.grandTotal', 'Grand Total')}:
                  </span>
                  <span className="summary-value total-display-enhanced">
                    {calculateTotals().grandTotal.toFixed(2)} {t('common.SAR')}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <div className="empty-text">
                {t(
                  'orders.createOrder.noItems',
                  'No items added yet. Click "Add Item" to start building your order.'
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Form Actions */}
        <div className="form-actions">
          <Space className="action-buttons order-actions-enhanced">
            <Button icon={<CloseOutlined />} onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              disabled={lineItems.length === 0}
            >
              {t('orders.createOrder.createOrder', 'Create Order')}
            </Button>
          </Space>
        </div>
      </Form>

      <CustomerClassificationModal
        visible={classificationModalVisible}
        customer={selectedCustomer}
        onClose={() => {
          setClassificationModalVisible(false);
          setPendingOrderData(null);
        }}
        onSuccess={handleClassificationSuccess}
      />
    </Modal>
  );
};

export default CreateOrderModal;
